import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Action } from '@/store/api/actions';
import { useJwtStore } from '@/store/modules/jwt';
import router from '@/router';

import { LoadingBar } from 'quasar';
import { notifyErr } from '@/utils/notify';

let baseUrl = import.meta.env.VUE_APP_API;
if (!baseUrl || baseUrl == '/') {
  baseUrl = '';
}

export const baseURL = baseUrl;

declare module 'axios' {
  interface AxiosRequestConfig {
    // A request the user did not ask for, such as a poll: it skips the 5 s GET
    // cache, the LoadingBar and the error toast, and the caller reports failures.
    background?: boolean;
  }
}

export const api = axios.create({
  baseURL: `${baseUrl}/${Action.API}`
});

// Make Axios play nice with Django CSRF
api.defaults.xsrfCookieName = 'csrftoken';
api.defaults.xsrfHeaderName = 'X-CSRFToken';

export function JwtExpired() {
  const exp = useJwtStore().DecodedPayload.exp;
  const now = Math.floor(new Date().getTime() / 1000);
  if (exp <= now) {
    return true;
  }
  return false;
}

async function refreshing() {
  const jwtStore = useJwtStore();
  while (jwtStore.RefreshingToken) {
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  return false;
}

let pendings: any = {};
let caches: any = {};
const cacheUtils = {
  getUniqueUrl: function(config: AxiosRequestConfig): string {
    // you can set the rule based on your own requirement
    return config.url + '&' + config.method;
  },
  clearAll: function() {
    pendings = {};
    caches = {};
  },
  isCached: function(config: AxiosRequestConfig) {
    const cacheExpireTime = 1000 * 5;
    const uniqueUrl = this.getUniqueUrl(config);
    const cache = caches[uniqueUrl];
    if (cache) {
      if (cache['setTime'] + cacheExpireTime > new Date().getTime()) {
        return true;
      }
    }
    return false;
  },
  isPending: function(config: AxiosRequestConfig) {
    const uniqueUrl = this.getUniqueUrl(config);
    if (!pendings[uniqueUrl]) {
      pendings[uniqueUrl] = [config];
      return false;
    } else {
      // console.log(`cache url: ${uniqueUrl}`);
      pendings[uniqueUrl].push(config);
      return true;
    }
  },
  setCachedResponse: function(
    config: AxiosRequestConfig,
    response: AxiosResponse
  ) {
    const uniqueUrl = this.getUniqueUrl(config);
    caches[uniqueUrl] = { response: response, setTime: new Date().getTime() };
    if (pendings[uniqueUrl]) {
      delete pendings[uniqueUrl];
      // pendings[uniqueUrl].forEach((configItem: any) => {
      //   configItem.isFinished = true;
      // });
    }
  },
  getError: function(config: AxiosRequestConfig) {
    const skipXHRError = new Error('skip') as any;
    skipXHRError.isSkipXHR = true;
    skipXHRError.requestConfig = config;
    return skipXHRError;
  },
  getCachedResponse: function(config: AxiosRequestConfig) {
    const uniqueUrl = this.getUniqueUrl(config);
    return caches[uniqueUrl]['response'];
  }
};

api.interceptors.request.use(
  async config => {
    const jwtStore = useJwtStore();
    if (config.url != `/${Action.RefreshToken}/` && jwtStore.RefreshingToken) {
      await refreshing();
    }
    if (
      config.url == `/${Action.ClearToken}/` ||
      config.url == `/${Action.Token}/`
    ) {
      cacheUtils.clearAll();
    }
    if (
      config.url != `/${Action.ClearToken}/` &&
      config.url != `/${Action.RefreshToken}/` &&
      config.url != `/${Action.Token}/` &&
      jwtStore.AccessToken &&
      jwtStore.AccessToken != ''
    ) {
      if (JwtExpired()) {
        jwtStore.refreshingToken();
        await jwtStore.refreshAccessToken();
      }
      if (config.headers) {
        if (jwtStore.AccessToken) {
          config.headers.Authorization = 'Bearer ' + jwtStore.AccessToken;
        } else {
          delete config.headers.Authorization;
          const error = cacheUtils.getError(config);
          throw error;
        }
      }
    } else {
      if (config.headers) {
        delete config.headers.Authorization;
      }
    }
    if (
      config.method?.toLowerCase() == 'get' &&
      !config.background &&
      !config.url?.includes(Action.Notifications)
    ) {
      if (cacheUtils.isCached(config)) {
        const error = cacheUtils.getError(config);
        throw error;
      }
      if (cacheUtils.isPending(config)) {
        return new Promise((resolve, reject) => {
          const interval = setInterval(() => {
            if (cacheUtils.isCached(config)) {
              clearInterval(interval);
              const error = cacheUtils.getError(config);
              reject(error);
            }
          }, 200);
        });
      } else {
        LoadingBar.start();
        LoadingBar.increment(0.5);
        // the head of cacheable requests queue, get the response by http request
        return config;
      }
    }

    return config;
  },
  error => {
    LoadingBar.stop();
    LoadingBar.increment(1);
    notifyErr(error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  config => {
    if (config.config.background) return config;
    LoadingBar.stop();
    LoadingBar.increment(1);
    cacheUtils.setCachedResponse(config.config, config);
    return config;
  },
  error => {
    if (!error.config?.background) {
      LoadingBar.stop();
      LoadingBar.increment(1);
    }
    if (error.isSkipXHR) {
      return cacheUtils.getCachedResponse(error.requestConfig);
    }

    const jwtStore = useJwtStore();

    /** Handler for logging out if refresh token is expired */
    if (error.response.config.url == `/${Action.RefreshToken}/`) {
      if (error.response.status == 401) {
        if (error.response.data.message != 'NoError') {
          jwtStore.clearJWT().then(() => {
            router.push({ name: 'LandingPage' });
            notifyErr('Session has been expired. Please login again.', { position: 'top-right' });
          });
          return Promise.reject(error);
        }
      }
    }

    /** Handler for refreshing access token */
    if (error.response.config.url != `/${Action.RefreshToken}/`) {
      if (
        (error.response.status == 403 || error.response.status == 401) &&
        error.response.data.message == 'Token is invalid or expired'
      ) {
        jwtStore.refreshingToken();
        jwtStore.clearJWT().then(() => {
          router.push({ name: 'LandingPage' });
          notifyErr('Session has been expired. Please login again.', { position: 'top-right' });
        });
        jwtStore.refreshAccessToken().then(() => {
          const config = error.response.config;
          if (jwtStore.AccessToken && jwtStore.AccessToken != '') {
            config.headers.Authorization = `Bearer ${jwtStore.AccessToken}`;
            api.request(config);
          }
          return config;
        });
        return Promise.reject(error);
      }
    }

    if (error.config?.background) return Promise.reject(error);

    /** Handler for error response with and without message */
    if (error.response.data.message) {
      if (error.response.data.message != 'NoError') {
        // timeout from backend is in seconds, convert to milliseconds (default: 5 seconds)
        const timeout = error.response.data.timeout
          ? error.response.data.timeout * 1000
          : 5000;
        notifyErr(error.response.data.message, { position: 'top', timeout });
      }
    } else {
      notifyErr('Network Error', { position: 'top' });
    }
    return Promise.reject(error);
  }
);

export async function postAPI<TResponse = unknown, TData = unknown>(
  url: string,
  data: TData,
  config?: AxiosRequestConfig
): Promise<TResponse> {
  return new Promise<TResponse>((resolve, reject) => {
    const apiResponse = api.post<TResponse>(`/${url}/`, data, config);
    apiResponse.then(
      result => {
        resolve(result.data);
      },
      error => {
        if (
          url == Action.RefreshToken &&
          (error.status_code == 400 || error.status_code == 401)
        ) {
          resolve({} as TResponse);
        }
        reject(error);
      }
    );
  });
}

export async function putAPI<TResponse = unknown, TData = unknown>(
  url: string,
  data: TData,
  config?: AxiosRequestConfig
): Promise<TResponse> {
  return new Promise<TResponse>((resolve, reject) => {
    const apiResponse = api.put<TResponse>(`/${url}/`, data, config);
    apiResponse.then(
      result => {
        resolve(result.data);
      },
      error => {
        if (
          url == Action.RefreshToken &&
          (error.status_code == 400 || error.status_code == 401)
        ) {
          resolve({} as TResponse);
        }
        reject(error);
      }
    );
  });
}

export async function patchAPI<TResponse = unknown, TData = unknown>(
  url: string,
  data: TData,
  config?: AxiosRequestConfig
): Promise<TResponse> {
  return new Promise<TResponse>((resolve, reject) => {
    const apiResponse = api.patch<TResponse>(`/${url}/`, data, config);
    apiResponse.then(
      result => {
        resolve(result.data);
      },
      error => {
        if (
          url == Action.RefreshToken &&
          (error.status_code == 400 || error.status_code == 401)
        ) {
          resolve({} as TResponse);
        }
        reject(error);
      }
    );
  });
}

export async function deleteAPI<TResponse = unknown>(
  url: string,
  config?: AxiosRequestConfig
): Promise<TResponse> {
  return new Promise<TResponse>((resolve, reject) => {
    const apiResponse = api.delete<TResponse>(`/${url}/`, config);
    apiResponse.then(
      result => {
        resolve(result.data);
      },
      error => {
        if (
          url == Action.RefreshToken &&
          (error.status_code == 400 || error.status_code == 401)
        ) {
          resolve({} as TResponse);
        }
        reject(error);
      }
    );
  });
}

export function getAPI<TResponse = unknown>(
  url: string,
  query = '',
  config?: AxiosRequestConfig
): Promise<TResponse> {
  return new Promise<TResponse>((resolve, reject) => {
    const apiResponse = api.get<TResponse>(`/${url}/${query}`, config);
    apiResponse.then(
      result => {
        resolve(result.data);
      },
      error => {
        reject(error);
      }
    );
  });
}
